import React from "react";
import { View, StyleSheet, Alert } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { MessageSquare, FileText } from "lucide-react-native";
import type { ContraceptionStackParamList } from "../ContraceptionNavigator";
import { CardTitleText, MoonDateField } from "@louise/moon-ui";
import { GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import {
  MedicalHistoryTextField,
  QuestionLayout,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useContraception } from "../ContraceptionContext";
import type { ContraceptionDetail } from "../ContraceptionContext";
import { CONTRACEPTION_METHOD_OPTIONS } from "../options";
import { useDateFormat } from "@src/packages/utils/useDateFormat";

export function ContraceptionDetailsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useContraception();
  const navigation =
    useNavigation<NativeStackNavigationProp<ContraceptionStackParamList>>();
  const { dateFormat } = useDateFormat();

  const getMethodLabel = (method: string): string => {
    const option = CONTRACEPTION_METHOD_OPTIONS.find((o) => o.id === method);
    return option ? t(option.labelKey) : method;
  };

  const updateContraception = (
    index: number,
    updates: Partial<ContraceptionDetail>,
  ) => {
    const updated = state.contraceptions.map((c, i) =>
      i === index ? { ...c, ...updates } : c,
    );
    updateState({ contraceptions: updated });
  };

  const handleSave = async () => {
    try {
      await save();
      navigation.navigate("ContraceptionComplete");
    } catch {
      Alert.alert(
        t("med_history.save_failed_title"),
        t("med_history.save_failed_message"),
        [{ text: t("med_history.save_failed_ok") }],
      );
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.gynae.contraception.details_title")}
      subtitle={t("med_history.gynae.contraception.details_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={false}
      saveLabel={t("med_history.gynae.contraception.continue")}
    >
      <View style={styles.container}>
        {state.contraceptions.map((contraception, index) => (
          <View
            key={`${contraception.method}-${index}`}
            style={[GLASS_CARD_STYLE, styles.contraceptionCard]}
          >
            <CardTitleText>
              {contraception.method === "other"
                ? t("med_history.gynae.contraception.other_card_title")
                : getMethodLabel(contraception.method)}
            </CardTitleText>

            {contraception.method === "other" && (
              <MedicalHistoryTextField
                label={t("med_history.gynae.contraception.other_label")}
                labelIcon={FileText}
                placeholder={t(
                  "med_history.gynae.contraception.other_placeholder",
                )}
                value={contraception.other ?? ""}
                onChangeText={(text) =>
                  updateContraception(index, { other: text || null })
                }
                style={{ width: "100%" }}
              />
            )}

            <MedicalHistoryTextField
              label={t("med_history.gynae.contraception.name_label")}
              labelIcon={FileText}
              placeholder={t(
                "med_history.gynae.contraception.name_placeholder",
              )}
              value={contraception.contraceptionName ?? ""}
              onChangeText={(text) =>
                updateContraception(index, {
                  contraceptionName: text || null,
                })
              }
              style={{ width: "100%" }}
            />

            <MoonDateField
              label={t("med_history.gynae.contraception.start_date_label")}
              placeholder={dateFormat}
              value={contraception.startDate ?? ""}
              onChange={(iso) =>
                updateContraception(index, { startDate: iso || null })
              }
            />

            <MoonDateField
              label={t("med_history.gynae.contraception.end_date_label")}
              placeholder={dateFormat}
              value={contraception.endDate ?? ""}
              onChange={(iso) =>
                updateContraception(index, { endDate: iso || null })
              }
            />

            {state.userGender !== "male" && (
              <MedicalHistoryTextField
                label={t(
                  "med_history.gynae.contraception.days_till_period_label",
                )}
                labelIcon={MessageSquare}
                placeholder={t(
                  "med_history.gynae.contraception.days_till_period_placeholder",
                )}
                value={
                  contraception.daysTillPeriodAfterStopping?.toString() ?? ""
                }
                onChangeText={(text) => {
                  const num = text ? parseInt(text, 10) : null;
                  updateContraception(index, {
                    daysTillPeriodAfterStopping:
                      num !== null && !isNaN(num) ? num : null,
                  });
                }}
                keyboardType="number-pad"
                style={{ width: "100%" }}
              />
            )}

            <MedicalHistoryTextField
              label={t("med_history.gynae.contraception.notes_label")}
              labelIcon={MessageSquare}
              placeholder={t(
                "med_history.gynae.contraception.notes_placeholder",
              )}
              value={contraception.notes ?? ""}
              onChangeText={(text) =>
                updateContraception(index, { notes: text || null })
              }
              multiline
              numberOfLines={2}
              style={{ width: "100%" }}
            />
          </View>
        ))}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
    gap: 16,
  },
  contraceptionCard: {
    gap: 16,
    padding: 16,
  },
});
